import { flattenDrfError } from '../../utils/apiUtils';

/** Base of the purchase ledger API (purchase_ledger/urls.py). */
export const PL_API = '/api/purchase-ledger';

export const invoiceUrl = (id, action) => `${PL_API}/invoices/${id}/${action ? `${action}/` : ''}`;
export const batchUrl = (id, action) => `${PL_API}/payment-batches/${id}/${action ? `${action}/` : ''}`;

/**
 * Translated message for a purchase ledger API error. The backend answers with
 * {detail, error_code, missing?}; the code wins when a translation exists, otherwise the
 * DRF detail text (already translated server-side) is shown.
 */
export function purchaseErrorMessage(t, error, fallbackKey = 'purchaseLedger.errors.generic') {
  const code = error?.response?.data?.error_code;
  if (code) {
    const key = `purchaseLedger.errors.${code}`;
    const translated = t(key, { defaultValue: '' });
    if (translated) return translated;
  }
  return flattenDrfError(error, t(fallbackKey));
}

export const STATUS_COLORS = {
  received: 'info',
  approved: 'success',
  disputed: 'warning',
  cancelled: 'default',
};

export const PAYMENT_STATE_COLORS = {
  unpaid: 'default',
  partial: 'warning',
  paid: 'success',
};

/** Flags carried on an invoice (machine codes, translated client-side). */
export const FLAG_SEVERITY = {
  new_iban: 'error',
  duplicate_suspect: 'error',
  totals_mismatch: 'warning',
  low_confidence: 'warning',
  vendor_unresolved: 'warning',
  no_due_date: 'info',
  match_diff: 'warning',
  foreign_currency: 'info',
  auto_number: 'info',
  invalid_reference: 'warning',
  invalid_iban: 'error',
  pages_truncated: 'info',
  attachment_truncated: 'info',
  unknown_type_code: 'info',
};

export const APPROVAL_MISSING_CODES = [
  'status',
  'vendor',
  'invoice_number',
  'invoice_date',
  'due_date',
  'total',
  'cost_category',
  'totals_mismatch',
  'new_iban',
  'duplicate_suspect',
  'invalid_iban',
];

/** Which header fields a flag or a low confidence score outlines in the editor. */
export const FLAG_FIELDS = {
  new_iban: ['payee_iban'],
  invalid_iban: ['payee_iban'],
  invalid_reference: ['reference_number'],
  totals_mismatch: ['total_net', 'total_vat', 'total_gross'],
  no_due_date: ['due_date'],
  vendor_unresolved: ['vendor_name_on_document'],
  foreign_currency: ['currency'],
};

export const LOW_CONFIDENCE_FIELD_MAP = {
  total_gross: ['total_gross'],
  payee_iban: ['payee_iban'],
  reference_number: ['reference_number'],
  due_date: ['due_date'],
  invoice_number: ['invoice_number'],
  vendor: ['vendor_name_on_document'],
  invoice_date: ['invoice_date'],
};

export function highlightedFields(invoice) {
  const fields = new Set();
  (invoice?.flags || []).forEach((flag) => (FLAG_FIELDS[flag] || []).forEach((f) => fields.add(f)));
  (invoice?.low_confidence_fields || []).forEach((name) =>
    (LOW_CONFIDENCE_FIELD_MAP[name] || []).forEach((f) => fields.add(f))
  );
  return fields;
}

export function toNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Browser-side UUID for idempotency keys (crypto.randomUUID is not in every WebView). */
export function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Trigger a browser download of a blob response, honouring Content-Disposition. */
export function downloadBlobResponse(response, fallbackName) {
  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  let filename = fallbackName;
  const header = response.headers?.['content-disposition'];
  if (header) {
    const match = header.match(/filename="?([^";]+)"?/);
    if (match) filename = match[1];
  }
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export const TABS = ['received', 'approved', 'in_batch', 'paid', 'disputed', 'all'];

export const KINDS = ['invoice', 'credit_note', 'receipt'];
export const PAYMENT_METHODS = ['bank_transfer', 'card', 'cash', 'other'];
export const RECEIPT_METHODS = ['card', 'cash', 'other'];
export const REFERENCE_TYPES = ['fi', 'rf', 'none'];
